// src/pages/Chat.tsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface User {
  id: string;
  email: string;
  stripe_customer_id?: string;
  subscription_status?: string;
}

interface Conversation {
  id: string;
  created_at: string;
}

export default function Chat() {
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch('/api/auth/me');
        
        if (res.status === 401) {
          navigate('/welcome');
          return;
        }
        
        if (res.status === 402) {
          navigate('/subscribe');
          return;
        }

        if (res.status === 403) {
          navigate('/inactive');
          return;
        }

        if (!res.ok) {
          throw new Error('Failed to fetch user');
        }

        const userData = await res.json();
        setUser(userData);
      } catch (err) {
        setError('Failed to load user data');
        console.error(err);
      }
    };

    fetchUser();
  }, [navigate]);

  useEffect(() => {
    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/api/chat/ws`);

      ws.onopen = () => console.log('WebSocket connected');
      ws.onmessage = (event) => {
        const response = JSON.parse(event.data);
        if (response.error) {
          setError(response.error);
          return;
        }
        
        setMessages(prev => {
          const lastMessage = prev[prev.length - 1];
          
          // If last message is from assistant and streaming, append to it
          if (lastMessage?.role === 'assistant') {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                content: lastMessage.content + response.completion
              }
            ];
          }
          
          // Otherwise create new assistant message
          return [...prev, {
            role: 'assistant',
            content: response.completion
          }];
        });
        
        setIsLoading(false);
      };
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error');
      };
      ws.onclose = () => setTimeout(connectWebSocket, 3000);
      wsRef.current = ws;
    };

    connectWebSocket();
    return () => wsRef.current?.close();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setIsLoading(true);

    try {
      // Create conversation if it doesn't exist
      if (!conversationId) {
        const convRes = await fetch('/api/chat/conversation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ modelId: 'claude-3-opus-20240229' })
        });
        
        if (!convRes.ok) {
          throw new Error('Failed to create conversation');
        }
        
        const { conversationId: newConvId } = await convRes.json();
        setConversationId(newConvId);
        await fetchConversations(); // Refresh conversation list
        
        // Save user message to database
        const userMessage: Message = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');

        await fetch('/api/chat/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId: newConvId,
            role: 'user',
            content: input
          })
        });

        wsRef.current?.send(JSON.stringify({ 
          prompt: input,
          conversationId: newConvId 
        }));
      } else {
        // Use existing conversation
        const userMessage: Message = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');

        await fetch('/api/chat/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId,
            role: 'user',
            content: input
          })
        });

        wsRef.current?.send(JSON.stringify({ 
          prompt: input,
          conversationId 
        }));
      }
    } catch (err) {
      console.error('Error submitting message:', err);
      setError('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoadingPortal(true);
    try {
      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError('Failed to access billing portal');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch('/api/chat/conversations');
      if (!res.ok) throw new Error('Failed to fetch conversations');
      const data = await res.json();
      setConversations(data);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleConversationSelect = async (id: string) => {
    setConversationId(id);
    try {
      const res = await fetch(`/api/chat/messages?conversationId=${id}`);
      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      setMessages(data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const createNewConversation = () => {
    setConversationId(null);
    setMessages([]); // Clear messages for new conversation
    inputRef.current?.focus();
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-red-50 p-4 rounded">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800">
      <Navigation />
      <div className="flex">
        <Sidebar 
          conversations={conversations}
          currentConversationId={conversationId}
          onConversationSelect={handleConversationSelect}
          onNewChat={createNewConversation}
        />
        <div className="flex-1">
          <div className="max-w-4xl mx-auto p-4">
            <div className="bg-zinc-800/50 border border-white/10 rounded-lg shadow-lg min-h-[calc(100vh-96px)] flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                          : 'bg-white/10 text-white'
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white/10 text-white rounded-lg p-3 animate-pulse">
                      Thinking...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <form onSubmit={handleSubmit} className="border-t border-white/10 p-4">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 rounded-lg bg-white/5 border-white/10 text-white placeholder-white/40 focus:ring-orange-500 focus:border-orange-500 px-4 py-2 leading-normal"
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="bg-gradient-to-r from-orange-500 via-purple-600 to-pink-500 hover:from-orange-600 hover:via-purple-700 hover:to-pink-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              </form>
            </div>

            {user?.stripe_customer_id && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleManageBilling}
                  disabled={isLoadingPortal}
                  className="inline-flex justify-center py-2 px-4 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingPortal ? 'Loading...' : 'Manage Billing'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}